import type { ServiceEvent } from "@/lib/service/types";
import {
  formatWeekRangeLabel,
  localWeekIsoBounds,
  parseLocalCalendarDay,
  weekDayKeys,
} from "@/lib/logistics/calendarLocalWeek";

export type ServiceCalendarListScope = "day" | "all_scheduled" | "all_unscheduled";

export type ServiceCalendarEventFilters = {
  queueFilter: string;
  rigFilter: string;
};

export type ServiceCalendarDayRollup = {
  date: string;
  unscheduled: number;
  scheduled: number;
  in_progress: number;
  complete: number;
  events: ServiceEvent[];
};

export function buildServiceWeekRollups(
  weekKeys: string[],
  events: ServiceEvent[],
  filters: ServiceCalendarEventFilters
): ServiceCalendarDayRollup[] {
  const filtered = applyServiceCalendarEventFilters(events, filters);

  const byDay = new Map<string, ServiceEvent[]>();
  for (const key of weekKeys) byDay.set(key, []);
  for (const event of filtered) {
    if (!event.scheduled_date) continue;
    if (!byDay.has(event.scheduled_date)) continue;
    byDay.get(event.scheduled_date)!.push(event);
  }

  return weekKeys.map((date) => {
    const dayEvents = [...(byDay.get(date) ?? [])].sort((a, b) =>
      (a.scheduled_window_label ?? "").localeCompare(b.scheduled_window_label ?? "")
    );
    return {
      date,
      unscheduled: dayEvents.filter((e) => e.status === "unscheduled").length,
      scheduled: dayEvents.filter((e) => e.status === "scheduled").length,
      in_progress: dayEvents.filter((e) => e.status === "in_progress").length,
      complete: dayEvents.filter((e) => e.status === "complete").length,
      events: dayEvents,
    };
  });
}

export { formatWeekRangeLabel, localWeekIsoBounds, parseLocalCalendarDay, weekDayKeys };

function isToday(date: string): boolean {
  const d = parseLocalCalendarDay(date);
  if (!d) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export { isToday };

export function eventMatchesRigFilter(event: ServiceEvent, rigFilter: string): boolean {
  if (rigFilter === "all") return true;
  return event.assigned_rig_id === rigFilter || event.required_rig_id === rigFilter;
}

export function applyServiceCalendarEventFilters(
  events: ServiceEvent[],
  filters: ServiceCalendarEventFilters
): ServiceEvent[] {
  return events.filter(
    (e) =>
      (filters.queueFilter === "all" || e.queue_id === filters.queueFilter) &&
      eventMatchesRigFilter(e, filters.rigFilter)
  );
}

export function filterEventsByQueue(events: ServiceEvent[], queueFilter: string): ServiceEvent[] {
  return applyServiceCalendarEventFilters(events, { queueFilter, rigFilter: "all" });
}

export function isPlannedServiceEvent(event: ServiceEvent): boolean {
  return Boolean(
    event.scheduled_date && event.status !== "unscheduled" && event.status !== "cancelled"
  );
}

export function buildScopeEvents(
  scope: ServiceCalendarListScope,
  opts: {
    selectedDayRollup: ServiceCalendarDayRollup | null;
    allOrgEvents: ServiceEvent[];
    filters: ServiceCalendarEventFilters;
  }
): ServiceEvent[] {
  const filtered = (events: ServiceEvent[]) =>
    applyServiceCalendarEventFilters(events, opts.filters);
  if (scope === "day") return opts.selectedDayRollup?.events ?? [];
  if (scope === "all_unscheduled") {
    return filtered(opts.allOrgEvents.filter((e) => e.status === "unscheduled"));
  }
  return filtered(opts.allOrgEvents.filter(isPlannedServiceEvent)).sort(
    (a, b) =>
      (a.scheduled_date ?? "").localeCompare(b.scheduled_date ?? "") ||
      (a.scheduled_window_label ?? "").localeCompare(b.scheduled_window_label ?? "")
  );
}

function formatDayHeading(date: string): string {
  return new Date(date + "T12:00:00").toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

export function scopePanelTitle(
  scope: ServiceCalendarListScope,
  selectedDay: string | null,
  todayDay: string
): string {
  if (scope === "all_scheduled") return "All scheduled";
  if (scope === "all_unscheduled") return "All unscheduled";
  if (!selectedDay) return "Day detail";
  if (selectedDay === todayDay) return "Today's schedule";
  return `${formatDayHeading(selectedDay)} — events`;
}
